/**
 * Agent Detail Page
 * 
 * Displays detailed information about a specific agent
 * - Fetches agent data by reference (UUID)
 * - Shows agent information
 * - Displays system details
 * - Shows status and metadata
 */

import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiMonitor, 
  FiServer, 
  FiCalendar,
  FiEdit2,
  FiRefreshCw,
  FiAlertCircle,
  FiLoader,
  FiCheckCircle,
  FiMinusCircle,
  FiXCircle,
  FiCpu,
  FiHash,
  FiActivity,
} from 'react-icons/fi';
import { useDetailPage } from '@/hooks';
import { agentService } from '@/services';
import Button from '@/components/Common/Button';
import Modal from '@/components/Common/Modal';
import AgentForm from '@/components/Forms/AgentForm';
import type { AgentFormData } from '@/components/Forms/AgentForm';
import styles from './AgentDetailPage.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  
  if (diffInMinutes < 1) return 'Agora';
  if (diffInMinutes < 60) return `${diffInMinutes} minutos atrás`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} horas atrás`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} dias atrás`;
  
  return formatDate(dateString);
};

/**
 * Get status label in Portuguese
 */
const getStatusLabel = (status?: string): string => {
  const statusLabels: Record<string, string> = {
    online: 'Online',
    offline: 'Offline',
    inactive: 'Inativo',
    error: 'Erro',
  };
  return statusLabels[status || 'offline'] || status || 'Desconhecido';
};

/**
 * Get status icon
 */
const getStatusIcon = (status?: string) => {
  const iconProps = { size: 16 };
  const statusIcons: Record<string, JSX.Element> = {
    online: <FiCheckCircle {...iconProps} />,
    offline: <FiMinusCircle {...iconProps} />,
    inactive: <FiMinusCircle {...iconProps} />,
    error: <FiXCircle {...iconProps} />,
  };
  return statusIcons[status || 'offline'] || null;
};

const AgentDetailPage: FC = () => {
  const { reference } = useParams<{ reference: string }>();

  /**
   * Use detail page hook
   */
  const {
    data: agent,
    isLoading,
    isError,
    error,
    refetch,
    isEditModalOpen,
    setIsEditModalOpen,
    handleBack,
    updateMutation,
  } = useDetailPage({
    reference,
    service: {
      getByReference: agentService.getAgentByReference,
      update: agentService.updateAgent,
    },
    queryKey: 'agent',
    navigationPath: '/agents',
    successMessages: {
      update: { 
        title: 'Agente atualizado!', 
        message: 'As alterações foram salvas com sucesso.' 
      },
    },
    errorMessages: {
      update: 'Erro ao atualizar agente',
    },
  });

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={styles.agentDetailPage}>
        <div className={styles.loadingContainer}>
          <FiLoader size={48} className={styles.spinner} />
          <p className={styles.loadingText}>Carregando dados do agente...</p>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (isError || !agent) {
    return (
      <div className={styles.agentDetailPage}>
        <div className={styles.errorContainer}>
          <FiAlertCircle size={64} className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Erro ao carregar agente</h2>
          <p className={styles.errorMessage}>
            {error instanceof Error ? error.message : 'Agente não encontrado'}
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <Button variant="secondary" onClick={handleBack}>
              Voltar
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Handle form submit
   */
  const handleFormSubmit = async (data: AgentFormData) => {
    await updateMutation.mutateAsync(data);
  };

  return (
    <div className={styles.agentDetailPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Voltar"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className={styles.headerTitle}>
            <h1 className={styles.title}>Detalhes do Agente</h1>
            <p className={styles.subtitle}>Informações completas do agente</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            iconLeft={<FiRefreshCw size={16} />}
            onClick={() => refetch()}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="md"
            iconLeft={<FiEdit2 size={16} />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Editar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Profile Card */}
        <div className={`${styles.card} ${styles.profileCard}`}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiMonitor size={20} />
            </div>
            <h2 className={styles.cardTitle}>Perfil do Agente</h2>
          </div>
          <div className={styles.profileContent}>
            <div className={styles.avatar}>
              <FiServer size={48} />
            </div>
            <div className={styles.profileInfo}>
              <h3 className={styles.agentName}>{agent.name}</h3>
              {agent.hostname && (
                <p className={styles.hostname}>{agent.hostname}</p>
              )}
              <div className={styles.agentMeta}>
                <div className={styles.metaItem}>
                  <span className={`${styles.statusBadge} ${styles[agent.status || 'offline']}`}>
                    {getStatusIcon(agent.status)}
                    {getStatusLabel(agent.status)}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <FiActivity size={16} className={styles.metaIcon} />
                  Última conexão: {formatRelativeTime(agent.lastSeenAt)}
                </div>
                <div className={styles.metaItem}>
                  <FiCalendar size={16} className={styles.metaIcon} />
                  Criado em {formatDate(agent.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agent Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiMonitor size={20} />
            </div>
            <h2 className={styles.cardTitle}>Informações do Agente</h2>
          </div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Nome</span>
              <span className={styles.infoValue}>{agent.name}</span>
            </div>
            {agent.identifier && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Identificador</span>
                <span className={styles.infoValue}>{agent.identifier}</span>
              </div>
            )}
            {agent.hostname && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Hostname</span>
                <span className={styles.infoValue}>{agent.hostname}</span>
              </div>
            )}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <span className={`${styles.statusBadge} ${styles[agent.status || 'offline']}`}>
                {getStatusIcon(agent.status)}
                {getStatusLabel(agent.status)}
              </span>
            </div>
            {agent.lastSeenAt && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Última Conexão</span>
                <span className={styles.infoValue}>
                  {formatDate(agent.lastSeenAt)}
                  <span className={styles.infoSecondary}>
                    {' '}({formatRelativeTime(agent.lastSeenAt)})
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* System Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiCpu size={20} />
            </div>
            <h2 className={styles.cardTitle}>Informações do Sistema</h2>
          </div>
          <div className={styles.infoList}>
            {agent.os && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sistema Operacional</span>
                <span className={styles.infoValue}>{agent.os}</span>
              </div>
            )}
            {agent.architecture && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Arquitetura</span>
                <span className={styles.infoValue}>{agent.architecture}</span>
              </div>
            )}
            {agent.pythonVersion && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Versão Python</span>
                <span className={styles.infoValue}>{agent.pythonVersion}</span>
              </div>
            )}
            {agent.agentVersion && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Versão do Agente</span>
                <span className={styles.infoValue}>{agent.agentVersion}</span>
              </div>
            )}
            {agent.machineId && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Machine ID</span>
                <span className={styles.infoValue}>{agent.machineId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Technical Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiHash size={20} />
            </div>
            <h2 className={styles.cardTitle}>Informações Técnicas</h2>
          </div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>ID do Agente</span>
              <span className={styles.infoValue}>{agent.id}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Referência (UUID)</span>
              <span className={styles.infoValue}>{agent.ref}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Organização (UUID)</span>
              <span className={styles.infoValue}>{agent.organizationRef}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Criado em</span>
              <span className={styles.infoValue}>{formatDate(agent.createdAt)}</span>
            </div>
            {agent.updatedAt && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Última atualização</span>
                <span className={styles.infoValue}>{formatDate(agent.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Agent Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Agente"
        description="Atualize as informações do agente"
        size="md"
      >
        <AgentForm
          mode="edit"
          initialValues={agent}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={updateMutation.isPending}
        />
      </Modal>
    </div>
  );
};

export default AgentDetailPage;
